/**
 * explodeMath.js — pure exploded-view maths.
 *
 *   pos(t) = base + SUM over ancestors(dir * dist * t) + own.dir * own.dist * t,  t in [0,1]
 *
 * Defaults when a node carries no "explode" override:
 *   dir  = normalize(childWorldCentroid - parentWorldCentroid)
 *          -> [0,1,0] when degenerate (|v| < 1e-6)
 *   dist = 0.6 * parent bounding-box diagonal
 *
 * NO three.js import — plain {x,y,z} objects or [x,y,z] arrays only, so this
 * stays unit-testable under bare node. Callers convert to Vector3 at the edge.
 *
 * Perf note (Pi 4 target): `buildExplodeVectors` precomputes the *cumulative*
 * displacement per node once, so the per-frame path is a single scalar multiply
 * per node and allocates nothing when given an output object to write into.
 */

export const DEGENERATE_EPSILON = 1e-6;
export const DEFAULT_DIRECTION = Object.freeze([0, 1, 0]);
export const DEFAULT_DISTANCE_FACTOR = 0.6;

/* ------------------------------------------------------------------ vectors */

/** Coerce [x,y,z] | {x,y,z} | null into a plain [x,y,z]. */
export function toArray3(v) {
  if (Array.isArray(v)) {
    return [Number(v[0]) || 0, Number(v[1]) || 0, Number(v[2]) || 0];
  }
  if (v && typeof v === 'object') {
    return [Number(v.x) || 0, Number(v.y) || 0, Number(v.z) || 0];
  }
  return [0, 0, 0];
}

export const add3 = (a, b) => [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
export const sub3 = (a, b) => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
export const scale3 = (a, s) => [a[0] * s, a[1] * s, a[2] * s];
export const length3 = (a) => Math.sqrt(a[0] * a[0] + a[1] * a[1] + a[2] * a[2]);

/** Normalize; returns [0,0,0] for a degenerate input (use defaultDirection for the fallback). */
export function normalize3(a) {
  const l = length3(a);
  if (l < DEGENERATE_EPSILON) return [0, 0, 0];
  return [a[0] / l, a[1] / l, a[2] / l];
}

/* ------------------------------------------------------------------ defaults */

/**
 * Default explode direction: away from the parent centroid.
 * Falls back to [0,1,0] when the two centroids coincide (|v| < 1e-6) or when
 * either centroid is unknown.
 * @param {number[]|{x,y,z}} childCentroid
 * @param {number[]|{x,y,z}} parentCentroid
 * @returns {number[]} unit vector
 */
export function defaultDirection(childCentroid, parentCentroid) {
  if (childCentroid == null || parentCentroid == null) return [...DEFAULT_DIRECTION];
  const v = sub3(toArray3(childCentroid), toArray3(parentCentroid));
  if (length3(v) < DEGENERATE_EPSILON) return [...DEFAULT_DIRECTION];
  return normalize3(v);
}

/**
 * Default explode distance: 0.6 * the parent's bounding-box diagonal.
 * @param {number} parentBBoxDiagonal
 * @returns {number} 0 when the diagonal is unknown / non-finite / negative
 */
export function defaultDistance(parentBBoxDiagonal) {
  const d = Number(parentBBoxDiagonal);
  if (!Number.isFinite(d) || d <= 0) return 0;
  return DEFAULT_DISTANCE_FACTOR * d;
}

/** Diagonal of an axis-aligned box given min/max corners. Convenience for callers. */
export function bboxDiagonal(min, max) {
  return length3(sub3(toArray3(max), toArray3(min)));
}

/* ------------------------------------------------------------------ lookups */

/**
 * The `centroids` argument is a plain map keyed by node id. Each value may be:
 *   [x,y,z] | {x,y,z}                                  -> centroid only
 *   {centroid:[x,y,z], bboxDiagonal:number}             -> centroid + size
 *   {centroid:{x,y,z}, diagonal:number}                 -> same, alias accepted
 * Unknown ids resolve to null, which drives the documented fallbacks.
 */
function entryOf(centroids, id) {
  if (!centroids || id == null) return null;
  const raw = (typeof centroids.get === 'function') ? centroids.get(id) : centroids[id];
  return raw ?? null;
}

export function centroidOf(centroids, id) {
  const raw = entryOf(centroids, id);
  if (raw == null) return null;
  if (Array.isArray(raw)) return toArray3(raw);
  if (raw.centroid != null) return toArray3(raw.centroid);
  if ('x' in raw || 'y' in raw || 'z' in raw) return toArray3(raw);
  return null;
}

export function diagonalOf(centroids, id) {
  const raw = entryOf(centroids, id);
  if (raw == null || Array.isArray(raw)) return 0;
  const d = raw.bboxDiagonal ?? raw.diagonal ?? raw.bbox_diagonal;
  const n = Number(d);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

/* ------------------------------------------------------------------ per-node vector */

/**
 * Resolve a node's OWN explode vector (not cumulative).
 * Honours a `explode: {dir:[x,y,z], dist:number}` override on the node,
 * otherwise applies the documented defaults against the parent.
 *
 * @param {string|object} node   node id or node object
 * @param {object} tree          result of buildTree()
 * @param {object} centroids     see centroidOf()
 * @returns {{dir:number[], dist:number}}
 */
export function resolveExplodeVector(node, tree, centroids) {
  const id = typeof node === 'string' ? node : (node?.id ?? null);
  const n = (typeof node === 'object' && node) ? node : (tree?.byId?.get?.(id) ?? null);
  if (!n) return { dir: [...DEFAULT_DIRECTION], dist: 0 };

  const parentId = n.parent ?? (tree?.parentOf ? tree.parentOf(id) : null);
  const ov = n.explode;

  let dir = null;
  let dist = null;
  if (ov && typeof ov === 'object') {
    if (ov.dir != null) {
      const d = normalize3(toArray3(ov.dir));
      dir = (length3(d) < DEGENERATE_EPSILON) ? [...DEFAULT_DIRECTION] : d;
    }
    if (ov.dist != null && Number.isFinite(Number(ov.dist))) dist = Number(ov.dist);
  }

  if (dir === null) {
    dir = (parentId == null)
      ? [...DEFAULT_DIRECTION]
      : defaultDirection(centroidOf(centroids, id), centroidOf(centroids, parentId));
  }
  if (dist === null) {
    dist = (parentId == null) ? 0 : defaultDistance(diagonalOf(centroids, parentId));
  }
  return { dir, dist };
}

/* ------------------------------------------------------------------ cumulative */

/**
 * Precompute, for every node in the tree, the total displacement applied at
 * t = 1: its own dir*dist plus that of every ancestor.
 *
 * @param {object} tree      result of buildTree()
 * @param {object} centroids see centroidOf()
 * @returns {Map<string, {dir:number[], dist:number, cumulative:number[]}>}
 */
export function buildExplodeVectors(tree, centroids) {
  const out = new Map();
  if (!tree || !Array.isArray(tree.order)) return out;
  for (const id of tree.order) {
    const node = tree.byId.get(id);
    const own = resolveExplodeVector(node, tree, centroids);
    const parentCum = node.parent != null ? (out.get(node.parent)?.cumulative ?? [0, 0, 0]) : [0, 0, 0];
    out.set(id, {
      dir: own.dir,
      dist: own.dist,
      cumulative: add3(parentCum, scale3(own.dir, own.dist)),
    });
  }
  return out;
}

/**
 * Cumulative offset for one node at parameter t.
 *
 *   offset(t) = ( SUM over ancestors(dir*dist) + own.dir*own.dist ) * t
 *
 * @param {string|object} node
 * @param {object} tree
 * @param {object} centroids
 * @param {number} t                 clamped to [0,1]
 * @param {number[]} [out]           optional array to write into (no allocation)
 * @returns {number[]} [dx,dy,dz]
 */
export function computeExplodeOffset(node, tree, centroids, t, out) {
  const id = typeof node === 'string' ? node : (node?.id ?? null);
  const target = out ?? [0, 0, 0];
  target[0] = 0; target[1] = 0; target[2] = 0;
  if (id == null || !tree || !tree.byId || !tree.byId.has(id)) return target;

  const tt = clamp01(t);
  const chain = [id, ...(tree.ancestorsOf ? tree.ancestorsOf(id) : [])];
  for (const cur of chain) {
    const { dir, dist } = resolveExplodeVector(tree.byId.get(cur), tree, centroids);
    const s = dist * tt;
    target[0] += dir[0] * s;
    target[1] += dir[1] * s;
    target[2] += dir[2] * s;
  }
  return target;
}

/**
 * Same result as computeExplodeOffset but reading a precomputed vector map —
 * this is the one to call from useFrame. Writes into `out`, allocates nothing.
 * @param {Map<string,{cumulative:number[]}>} vectors  from buildExplodeVectors
 */
export function explodeOffsetFromVectors(vectors, id, t, out) {
  const target = out ?? [0, 0, 0];
  const v = vectors?.get?.(id);
  const tt = clamp01(t);
  if (!v) { target[0] = 0; target[1] = 0; target[2] = 0; return target; }
  target[0] = v.cumulative[0] * tt;
  target[1] = v.cumulative[1] * tt;
  target[2] = v.cumulative[2] * tt;
  return target;
}

/**
 * Final world position: base + offset(t).
 * @param {number[]|{x,y,z}} base
 * @returns {number[]}
 */
export function computeExplodePosition(base, node, tree, centroids, t, out) {
  const b = toArray3(base);
  const target = computeExplodeOffset(node, tree, centroids, t, out);
  target[0] += b[0]; target[1] += b[1]; target[2] += b[2];
  return target;
}

/* ------------------------------------------------------------------ animation */

export function clamp01(t) {
  const n = Number(t);
  if (!Number.isFinite(n)) return 0;
  return n < 0 ? 0 : (n > 1 ? 1 : n);
}

/**
 * Frame-rate independent damped lerp. lambda ~8..12 per the spec.
 *   next = target + (current - target) * exp(-lambda * dt)
 * @returns {number}
 */
export function damp(current, target, lambda = 10, dt = 1 / 60) {
  const l = Number.isFinite(Number(lambda)) ? Number(lambda) : 10;
  const d = Number.isFinite(Number(dt)) ? Number(dt) : 0;
  const next = target + (current - target) * Math.exp(-l * d);
  return Math.abs(next - target) < 1e-4 ? target : next;
}

export default computeExplodeOffset;
